import React, { useState } from "react";
import { PackageCheck, Loader2, ArrowRight } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { IMG, SERVICES } from "@/lib/siteData";
import { useLang } from "@/context/LangContext";

const EMPTY = {
    company_name: "", contact_name: "", email: "", phone: "", goods_type: "", weight_kg: "",
    pickup_city: "", delivery_city: "", desired_date: "", message: "", transport: "",
};
const inputCls = "w-full rounded-md bg-white border border-slate-300 px-4 py-3 text-[#0b2545] placeholder:text-slate-500 focus:outline-none focus:border-[#f26b1d]";

export default function DevisPage() {
    const { t, lang } = useLang();
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { transport, ...rest } = form;
            const tags = `${transport ? `[${transport}] ` : ""}${lang === "en" ? "[EN] " : ""}`;
            await api.post("/public/quote", { ...rest, message: `${tags}${rest.message}`, weight_kg: Number(form.weight_kg) || 0 });
            setDone(true);
            toast.success(t("Demande envoyée. Nous revenons vers vous rapidement.", "Request sent. We will get back to you shortly."));
            setForm(EMPTY);
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <PageHero testid="devis-hero" eyebrow={t("Demande de devis", "Quote request")} img={IMG.hero} title={t("Décrivez votre expédition", "Describe your shipment")}
                text={t("Nous vous proposons une solution routière, maritime, aérienne ou multimodale adaptée à votre besoin.", "We offer a road, sea, air or multimodal solution suited to your needs.")} />
            <section className="max-w-5xl mx-auto px-5 lg:px-8 py-14">
                <form onSubmit={submit} className="grid gap-5 lg:grid-cols-2 bg-white border border-slate-200 shadow-sm rounded-md p-6 lg:p-10">
                    <Field label={t("Société *", "Company *")} testid="devis-company" value={form.company_name} onChange={update("company_name")} required />
                    <Field label="Contact *" testid="devis-contact" value={form.contact_name} onChange={update("contact_name")} required />
                    <Field label="Email *" type="email" testid="devis-email" value={form.email} onChange={update("email")} required />
                    <Field label={t("Téléphone", "Phone")} testid="devis-phone" value={form.phone} onChange={update("phone")} />
                    <div>
                        <label className="text-sm text-slate-700 mb-2 block">{t("Type de transport", "Transport type")}</label>
                        <select data-testid="devis-transport" value={form.transport} onChange={update("transport")} className={inputCls}>
                            <option value="">{t("— Choisir —", "— Choose —")}</option>
                            {SERVICES.map((s) => <option key={s.slug} value={s.title}>{t(s.title, s.titleEn)}</option>)}
                        </select>
                    </div>
                    <Field label={t("Type de marchandise *", "Type of goods *")} testid="devis-goods" value={form.goods_type} onChange={update("goods_type")} required placeholder={t("ex. palettes, conteneur 40'…", "e.g. pallets, 40' container…")} />
                    <Field label={t("Ville de départ *", "Departure city *")} testid="devis-pickup" value={form.pickup_city} onChange={update("pickup_city")} required />
                    <Field label={t("Ville d'arrivée *", "Arrival city *")} testid="devis-delivery" value={form.delivery_city} onChange={update("delivery_city")} required />
                    <Field label={t("Poids (kg)", "Weight (kg)")} type="number" testid="devis-weight" value={form.weight_kg} onChange={update("weight_kg")} />
                    <Field label={t("Date souhaitée", "Preferred date")} type="date" lang={lang} testid="devis-date" value={form.desired_date} onChange={update("desired_date")} />
                    <div className="lg:col-span-2">
                        <label className="text-sm text-slate-700 mb-2 block">{t("Message complémentaire", "Additional message")}</label>
                        <textarea data-testid="devis-message" rows={4} value={form.message} onChange={update("message")} className={inputCls} placeholder={t("Dimensions, conditions particulières, délai critique…", "Dimensions, special conditions, critical deadline…")} />
                    </div>
                    <div className="lg:col-span-2 flex items-center gap-4 justify-end">
                        {done && <span className="text-green-700 text-sm flex items-center gap-2" data-testid="devis-success"><PackageCheck size={16} /> {t("Demande bien reçue", "Request received")}</span>}
                        <button type="submit" data-testid="devis-form-submit-button" disabled={loading} className="kz-btn-primary">
                            {loading && <Loader2 className="animate-spin" size={16} />} {t("Envoyer ma demande", "Send my request")} <ArrowRight size={15} />
                        </button>
                    </div>
                </form>
            </section>
        </>
    );
}

function Field({ label, testid, type = "text", ...props }) {
    return (
        <div>
            <label className="text-sm text-slate-700 mb-2 block">{label}</label>
            <input data-testid={testid} type={type} {...props} className={inputCls} />
        </div>
    );
}
